import type { RefObject } from "react";
import { useEffect, useState } from "react";
import { stripLangPrefix } from "i18n/urlLocale";
import { withCustomProductPage } from "components/common/customProductPage";

const ONELINK_TEMPLATE = "https://stippl.onelink.me/cgUq";
// Every link we hand out uses the template path WITH a trailing slash (`/cgUq/?pid=…`). iOS only opens
// the app from a OneLink whose path matches the domain's apple-app-site-association, which AppsFlyer
// serves as `"paths": ["/cgUq/*"]` for both stippl.onelink.me and onelink.stippl.io. The bare
// `/cgUq?pid=…` form does not match, so Safari loaded it and AppsFlyer sent an installed user to the
// App Store instead of the app (123yb69552x, real iPhone, 2026-10-01). AppsFlyer's server answers both
// forms identically (same 301 to the store when the app does not open), and Android's intent filter
// matches the whole host, so the slash only changes what iOS does with the link.
const ONELINK_LINK_BASE = `${ONELINK_TEMPLATE}/`;

/** Rewrites a OneLink on the bare template path (`…/cgUq?…`) to the universal-link form (`…/cgUq/?…`). */
export function withUniversalLinkPath(url: string): string {
	try {
		const parsed = new URL(url);
		if (parsed.pathname === new URL(ONELINK_TEMPLATE).pathname) {
			parsed.pathname = `${parsed.pathname}/`;
			return parsed.toString();
		}
		return url;
	} catch {
		return url;
	}
}
const SMART_SCRIPT_SRC = "https://onelinksmartscript.appsflyersdk.com/onelink-smart-script-latest.js";
const SCRIPT_ID = "af-smart-script";
const READY_EVENT = "af-onelink-ready";

// Marketing feature/product landing pages — mirrors the route list in
// components/website/components/navigation/WebsiteRoutes.tsx. Keep this in sync with that file;
// nothing enforces it automatically.
const FEATURE_LANDING_PAGES = new Set([
	"/budget-planner",
	"/expense-tracker",
	"/esim",
	"/itinerary-planner",
	"/your-map",
	"/social-travel-platform",
	"/packing-list",
	"/scratch-map",
	"/ai-travel-planner",
	"/chatgpt-travel-planner",
	"/claude-travel-planner",
	"/pro",
	"/photobook",
	"/travel-journal",
	"/travel-tracker",
	"/travel-reel",
]);

// First path segments that are NOT a public profile — every single-segment route in
// components/common/Routes.tsx (e.g. "/settings", "/discover"), which all shadow "/:username",
// the last single-segment route declared in that file. Keep this in sync with Routes.tsx;
// nothing enforces it automatically.
const RESERVED_FIRST_SEGMENTS = new Set([
	"accept-invite",
	"bookings",
	"business",
	"business-deck",
	"business-onboarding",
	"business-terms",
	"disclaimer",
	"discover",
	"explore",
	"faq",
	"forgotpassword",
	"home",
	"login",
	"map",
	"maptest",
	"mycollection",
	"mymap",
	"onboarding",
	"paywall",
	"pricing",
	"privacy",
	"profile",
	"register",
	"save",
	"settings",
	"stays-terms",
	"terms",
	"usermanagement",
	"security.txt",
]);

// Path shapes that identify a trip/profile share page — matched on structure, not charset. Usernames
// and trip names in real data contain spaces, emoji, dots and even slashes (e.g. "/douglas.96/trip/Euro/Africa Trip"),
// so these stay permissive rather than anchoring on [a-z0-9-].
const TRIP_SHARE_PATH_PATTERNS = [
	/^\/share\/.+/,
	/^\/reel\/.+/,
	/^\/trip\/.+/,
	/^\/[^/]+\/trip\/.+/, // /<username>/trip/<...>
	/^\/b\/[^/]+\/trip\/.+/, // /b/<org>/trip/<...>
];

/**
 * Buckets a pathname into a coarse AppsFlyer campaign name. Replaces the single "share_view"
 * default that used to catch every untagged landing on stippl.io — measured reality was 79%
 * marketing website (51% the homepage alone) and only 11.9% actual trip share pages, so the old
 * name was actively misleading and hid our best-converting acquisition surface. A present
 * utm_campaign always wins over this; this is only the fallback bucket.
 *
 * Never throws — this runs during page load for every "download the app" CTA, so a bad/unexpected
 * path must degrade to web_other rather than break the link.
 *
 * Timing: generate() runs when the async Smart Script finishes loading, so this reads whatever page
 * the user is on at that moment — the landing page in practice, but the second page if they navigated
 * within the script's load window. That is the same read afSub1 already does, in the same call, so
 * campaign and af_sub1 can never disagree with each other. Deliberately not "fixed" to a captured
 * landing path: that would desynchronise the two.
 */
export function campaignForPath(pathname: string): string {
	if (typeof pathname !== "string" || pathname.length === 0) return "web_other";

	try {
		const { basePath } = stripLangPrefix(pathname);
		const normalized = basePath.toLowerCase().replace(/\/+$/, "") || "/";

		if (normalized === "/" || normalized === "/home") return "web_home";
		if (normalized === "/blog" || normalized.startsWith("/blog/")) return "web_blog";
		if (normalized === "/discover" || normalized.startsWith("/discover/")) return "web_discover";
		if (FEATURE_LANDING_PAGES.has(normalized)) return "web_feature_lp";
		if (normalized.startsWith("/join/")) return "join_link";
		// Widened from /login + /register: AppsFlyer raw installs, website media source, 29 Aug-28
		// Sep, show 17 installs bucketed web_other, 15 of them carrying af_sub1=/usermanagement (the
		// Firebase email-action page -- email verification + password reset). /forgotpassword and
		// /accept-invite are the same auth flow by construction, so they're folded in too.
		if (
			normalized === "/login" ||
			normalized === "/register" ||
			normalized === "/forgotpassword" ||
			normalized === "/usermanagement" ||
			normalized === "/accept-invite"
		)
			return "web_auth";
		// The "Make this route yours" page reached from a suggested route on the country page
		// (123yb69474b) -- was landing in web_other, indistinguishable from everything else there.
		if (normalized.startsWith("/routepreview/")) return "web_route_preview";
		// The city-guide page shared from social posts and blogs, /listpreview/:listId.
		if (normalized.startsWith("/listpreview/")) return "web_list_preview";
		if (TRIP_SHARE_PATH_PATTERNS.some((pattern) => pattern.test(normalized))) return "trip_share_page";

		// A public profile is just "/<username>" — a single, non-reserved path segment.
		const firstSegment = normalized.slice(1);
		if (firstSegment.length > 0 && !firstSegment.includes("/") && !RESERVED_FIRST_SEGMENTS.has(firstSegment)) {
			return "profile_page";
		}

		return "web_other";
	} catch (e: any) {
		console.error("[AppsFlyer] campaignForPath failed:", { pathname, error: e?.message || e });
		return "web_other";
	}
}

// The path-less value useAppDownloadUrl()/usePlacementOnelinkUrl() (useAppStoreLink.ts) used to
// return before mount, kept exported for any remaining direct import. pid=website is the same
// default every path below falls back to when nothing more specific is known, so even a visitor
// who never gets past this static value is attributed as web-originated rather than landing on an
// unattributed direct apps.apple.com/play.google.com link (123yb6937h3).
export const STATIC_ONELINK_URL = withCustomProductPage(`${ONELINK_LINK_BASE}?pid=website`);

// The pre-mount value useAppDownloadUrl()/usePlacementOnelinkUrl() (useAppStoreLink.ts) actually
// return now — on the server and on the very first client render. `pathname` is read by the
// caller from react-router's useLocation() rather than window.location here, which is what keeps
// this pure and hydration-safe: server (StaticRouter) and client (BrowserRouter) resolve the same
// route path for the same request, so server and client markup are byte-identical (same reasoning
// LanguageSwitcher.tsx and MarketingShell.tsx already rely on for their own useLocation() calls).
//
// Replaces the old path-less STATIC_ONELINK_URL default (#1353): AppsFlyer raw installs on the
// website media source carried 8 installs with campaign "None" and an empty af_sub1 over 29 Aug-28
// Sep — taps on server-rendered marketing HTML before hydration, landing pre-mount and staying
// that way. Carrying c/af_sub1 from the very first render closes that gap.
//
// `af_channel=prehydration` keeps these clicks separable now that they no longer sit in "None".
// That bucket was 160 clicks for 8 installs over the same window, mostly fetches of the server
// HTML's links rather than taps, and "the campaign only exists after hydration" is the test
// integrations/appsflyer.md uses to tell a JS-rendering crawler from a plain one. Same param
// forQrCode() uses for `qr`: `c` is which page, `af_channel` is how the link was reached.
export function staticOnelinkUrl(pathname: string): string {
	return withCustomProductPage(
		`${ONELINK_LINK_BASE}?pid=website&c=${encodeURIComponent(campaignForPath(pathname))}&af_sub1=${encodeURIComponent(pathname)}&af_channel=prehydration`
	);
}

// OneLink itself does OS-based smart-redirect server-side — the Smart Script only enriches it with
// web-to-app attribution. When the script is blocked (ad blockers routinely block appsflyersdk.com),
// this static link still routes users to the right store, just without the extra attribution params.
//
// pid and c mirror exactly what generate()'s afParameters do below: pid reads utm_source off the
// current URL (falling back to "website"), c reads utm_campaign (falling back to campaignForPath).
// Added 2026-08-31 — before this, the blocked path always hardcoded pid=website and
// c=campaignForPath(path), which silently threw away a real utm_campaign whenever one was present
// in the query string (e.g. a /join/... trip-invite link that carries utm_campaign=trip_invite).
// Reading the same params the live generate() path reads, rather than reimplementing a second set
// of defaults, is what keeps the live and blocked paths consistent in AppsFlyer's raw data — a
// "fallback" is supposed to be a degraded version of the real click, not a differently-attributed one.
//
// af_sub1 carries the page path so we still know where the click came from even without the Smart
// Script — same param the live path below sets, so both are consistent in AppsFlyer's raw data.
// Deliberately carries no extra "this was the blocked path" marker. DownloadAppQrCard and
// DownloadAppModal render this exact URL as a QR code when the Smart Script never resolved, and QR
// capacity is quantised: an `af_sub2=script_blocked` took the homepage code from 37 modules to 41,
// and a long trip path from 45 to 49. A diagnostic that degrades the fallback it is diagnosing is
// not worth four modules — the blocked cohort can be recovered from af_sub1 + the absent
// af_js_web/af_ss_ver params instead, at no cost to the code that has to scan.
export function getFallbackOnelinkUrl(): string {
	const path = typeof window === "undefined" ? "" : window.location.pathname;

	// Defaults match generate()'s afParameters exactly (mediaSource → "website", campaign →
	// campaignForPath). Only overridden below when the current URL actually carries the utm param.
	let pid = "website";
	let campaign = campaignForPath(path);

	try {
		if (typeof window !== "undefined") {
			const searchParams = new URLSearchParams(window.location.search);
			const utmSource = searchParams.get("utm_source");
			const utmCampaign = searchParams.get("utm_campaign");
			if (utmSource) pid = clampForQr(utmSource);
			if (utmCampaign) campaign = clampForQr(utmCampaign);
		}
	} catch (e: any) {
		// Never let a malformed query string take down the fallback link — fall through with
		// whatever of pid/campaign was resolved before the failure (the "website"/campaignForPath
		// defaults, most likely).
		console.error("[AppsFlyer] getFallbackOnelinkUrl failed to read utm params:", { error: e?.message || e });
	}

	return withCustomProductPage(`${ONELINK_LINK_BASE}?pid=${encodeURIComponent(pid)}&c=${encodeURIComponent(campaign)}&af_sub1=${encodeURIComponent(path)}`);
}

// Bounds a raw utm_source/utm_campaign value (ultimately end-user, i.e. attacker, controlled — it's
// whatever the incoming URL's query string says) before it's folded into the fallback link above.
// That link gets rendered as a QR code (see the block comment above getFallbackOnelinkUrl), and QR
// capacity is tight enough that a handful of extra characters bumps the version — the homepage
// fallback is pinned to <=84 chars to stay at version 5. 64 chars is generous for any real UTM value
// while still guaranteeing an unexpectedly long (or deliberately abusive) param can't blow that budget.
function clampForQr(value: string): string {
	return value.slice(0, 64);
}

/**
 * Returns `url` with its af_sub1 param set to `path` (added if absent, replaced if present).
 *
 * The Smart Script generates the attributed URL once per page load and caches it — regenerating it
 * on every client-side navigation would risk losing the first-touch mediaSource/campaign values
 * (those come from utm_source/utm_campaign on the very first landing page's URL; a client-routed
 * page later on won't have those query params, so a naive regenerate would silently overwrite real
 * campaign attribution with the generic defaults). Rewriting just af_sub1 on the already-cached URL
 * sidesteps that entirely — it never touches mediaSource/campaign/anything else.
 *
 * Pure and side-effect-free so it's trivially testable; callers pass in the current path rather than
 * this reading `window` itself.
 */
export function withCurrentPath(url: string, path: string): string {
	try {
		const parsed = new URL(url);
		parsed.searchParams.set("af_sub1", path);
		return parsed.toString();
	} catch (e: any) {
		console.error("[AppsFlyer] withCurrentPath failed to parse URL:", { url, error: e?.message || e });
		return url;
	}
}

/**
 * Returns `url` with its af_adset param set to `adset` (added if absent, replaced if present).
 *
 * Every install affordance on a blog/discover page (the header's "Get the app" button, the
 * floating `DownloadAppQrCard`, the end-of-page `WaveDownloadCTA`/`DownloadBanner`, and now the
 * in-flow inline install block — work/install-card-mobile.md's A3) rides the same OneLink and the
 * same `c=web_blog`/`c=web_discover` campaign, so without a further tag none of them is separately
 * measurable. AppsFlyer's aggregated reporting API groups by *Adset* but not by sub params beyond
 * `af_sub1` (already spent on the page path by `withCurrentPath` above), which is why placement
 * rides `af_adset` rather than a second `af_sub` value.
 *
 * Independent of `withCurrentPath` — the two set different query params, so this survives being
 * called before or after it, or before or after the af_sub1 rewrite `useAppsflyerOneLink` callers
 * do on every render.
 */
export function withAdSet(url: string, adset: string): string {
	try {
		const parsed = new URL(url);
		parsed.searchParams.set("af_adset", adset);
		return parsed.toString();
	} catch (e: any) {
		console.error("[AppsFlyer] withAdSet failed to parse URL:", { url, error: e?.message || e });
		return url;
	}
}

/**
 * Returns `url` with OneLink's own deep-link params set: `deep_link_value` (what the app should open)
 * and, optionally, `deep_link_sub1`. Added if absent, replaced if present; an empty `sub1` leaves any
 * existing `deep_link_sub1` untouched.
 *
 * This is the routing payload, not attribution: AppsFlyer hands both values to the app's
 * `onDeepLink` callback on first launch after an install from this link (deferred) and on every
 * launch through it when the app is already installed (direct), so the app can open the thing the
 * user tapped instead of generic onboarding. `af_sub1` already carries the page path, but it is a
 * reporting field and routes nobody (work/invite-install-continuity.md, phase 2).
 *
 * `value` is path-shaped (`join/<tripId>`, later `user/<username>`) so one app-side handler can route
 * every owned surface. Independent of `withCurrentPath`/`withAdSet` — different params.
 */
export function withDeepLink(url: string, value: string, sub1?: string): string {
	try {
		const parsed = new URL(withUniversalLinkPath(url));
		parsed.searchParams.set("deep_link_value", value);
		if (sub1) parsed.searchParams.set("deep_link_sub1", sub1);
		return parsed.toString();
	} catch (e: any) {
		console.error("[AppsFlyer] withDeepLink failed to parse URL:", { url, error: e?.message || e });
		return url;
	}
}

/**
 * Strip the smart-script's own bookkeeping and mark the link as scanned rather than clicked.
 *
 * `af_js_web`, `af_ss_ver` and `af_ss_ui` tell AppsFlyer that its browser script generated the
 * URL. That is true of a link someone clicks and meaningless on a printed square: nobody is
 * running the web script when they point a phone at it. They cost ~46 characters, and QR
 * capacity is quantised — dropping them takes the code from 49 modules (version 8) to 41,
 * which is the difference between scanning and not at the size these cards render.
 *
 * `af_channel=qr` is added rather than overwriting `c`, and the two now answer different
 * questions: `c` is which surface the scan came from (`campaignForPath`, added in #1103), and
 * `af_channel` is how the user engaged with it. Together they distinguish a QR scan on the
 * homepage from a click on the homepage — which neither field gives you alone. Overwriting `c`
 * with `qr` would collapse the surface dimension to buy the engagement one; a channel is
 * additive, so both stay answerable. That is what closes open question 3 on `86cbat0gt`.
 *
 * `af_ios_store_cpp` (the Custom Product Page id, see customProductPage.ts) is dropped here too. It is
 * 54 characters, which is one to two QR versions on every card (measured 2026-10-13 with the card's
 * own payloads: the 124px floating card goes 41 -> 53 modules on the homepage, 2.88 -> 2.25 px per
 * module, under the 2.43 of the 49-module code above that did not scan; the 80px invite card 53 -> 57). A scanned code is
 * attributed by the rest of the link; only the per-surface page split is lost for QR scans.
 *
 * Note this only trims the *live* Smart Script URL. `getFallbackOnelinkUrl()` — what an
 * ad-blocked visitor gets, which is precisely when someone is scanning rather than clicking —
 * never carried `af_js_web`/`af_ss_ver`/`af_ss_ui`, so it gets none of the saving above. Its
 * length is dominated by `af_sub1` carrying the whole path, and a long locale-prefixed blog slug
 * still reaches version 8 there. Shortening that means changing what `af_sub1` stores.
 */
export function forQrCode(url: string): string {
	try {
		const parsed = new URL(url);
		for (const key of ["af_js_web", "af_ss_ver", "af_ss_ui", "af_ios_store_cpp"]) parsed.searchParams.delete(key);
		parsed.searchParams.set("af_channel", "qr");
		return parsed.toString();
	} catch (e: any) {
		console.error("[AppsFlyer] forQrCode failed to parse URL:", { url, error: e?.message || e });
		return url;
	}
}

let cachedUrl: string | null = null;
let loading = false;

export function getAppsflyerOneLinkUrl(): string | null {
	return cachedUrl;
}

function generate() {
	try {
		const result = (window as any).AF_SMART_SCRIPT?.generateOneLinkURL({
			oneLinkURL: ONELINK_TEMPLATE,
			afParameters: {
				mediaSource: { keys: ["utm_source"], defaultValue: "website" },
				campaign: { keys: ["utm_campaign"], defaultValue: campaignForPath(window.location.pathname) },
				// Always the current page path — keys: [] forces the default (there's no incoming URL
				// param for this, we just want wherever the user is right now).
				afSub1: { keys: [], defaultValue: window.location.pathname },
				afCustom: [{ paramKey: "af_ss_ui", defaultValue: "true" }],
			},
		});
		cachedUrl = result?.clickURL ? withCustomProductPage(withUniversalLinkPath(result.clickURL)) : null;
		if (cachedUrl) {
			window.dispatchEvent(new CustomEvent(READY_EVENT));
		}
	} catch (e: any) {
		console.error("[AppsFlyer] generateOneLinkURL failed:", { error: e?.message || e });
	}
}

export function initAppsflyerOneLink(): void {
	if (typeof window === "undefined" || typeof document === "undefined") return;
	if (cachedUrl || loading) return;

	if (document.getElementById(SCRIPT_ID)) {
		if ((window as any).AF_SMART_SCRIPT) {
			generate();
		}
		return;
	}

	loading = true;
	const script = document.createElement("script");
	script.id = SCRIPT_ID;
	script.src = SMART_SCRIPT_SRC;
	script.async = true;
	script.onload = () => generate();
	script.onerror = () => {
		console.error("[AppsFlyer] Smart Script failed to load");
	};
	document.head.appendChild(script);
}

export default function useAppsflyerOneLink(): string | null {
	const [url, setUrl] = useState<string | null>(getAppsflyerOneLinkUrl());

	useEffect(() => {
		if (url) return undefined;
		const handler = () => setUrl(getAppsflyerOneLinkUrl());
		window.addEventListener(READY_EVENT, handler);
		// in case it became ready between render and effect
		if (getAppsflyerOneLinkUrl()) setUrl(getAppsflyerOneLinkUrl());
		return () => window.removeEventListener(READY_EVENT, handler);
	}, [url]);

	return url;
}

export interface QrCodeRenderOptions {
	colorDark?: string;
	colorLight?: string;
	// Intended CSS pixel box. Defaults to `canvas.width` when omitted (legacy behavior) — but that
	// attribute gets overwritten with the DPR-scaled backing-store size by qrcode's renderer, so a
	// re-run of this effect (React StrictMode's double-invoke in dev, a remount, etc.) would read back
	// its own scaled output and scale it again on top, compounding the size on every run. Pass `size`
	// explicitly to sidestep that.
	size?: number;
}

/**
 * Draws a QR code for `url` into `canvasRef.current`, self-generated client-side (not delegated to
 * AF_SMART_SCRIPT.displayQrCode — that call proved inconsistent about honoring color styling across
 * simultaneous instances). Gives full, reliable control over styling and works the same way whether
 * `url` is the real attributed OneLink or the static getFallbackOnelinkUrl() fallback.
 *
 * Renders at size * devicePixelRatio and lets the fixed CSS box (set in each caller's SCSS)
 * downsample it — otherwise the backing store is stuck at the CSS pixel count and blurs when the
 * browser upscales it for HiDPI/Retina screens.
 *
 * `active` should stay false until the canvas is actually mounted in the DOM (e.g. gated on the same
 * isOpen/visible condition as the component itself) — the canvas must exist before this can draw into it.
 */
export function useQrCodeCanvas(canvasRef: RefObject<HTMLCanvasElement>, active: boolean, url: string, options?: QrCodeRenderOptions): boolean {
	const [rendered, setRendered] = useState(false);
	const { colorDark = "#1b3d49", colorLight = "#ffffff", size } = options ?? {};

	useEffect(() => {
		if (!active) return undefined;
		const canvas = canvasRef.current;
		if (!canvas) return undefined;
		let cancelled = false;

		(async () => {
			const QRCode = await import("qrcode");
			if (cancelled) return;
			const displaySize = size ?? canvas.width;
			const dpr = window.devicePixelRatio || 1;
			await QRCode.toCanvas(canvas, url, {
				width: Math.round(displaySize * dpr),
				margin: 1,
				errorCorrectionLevel: "M",
				color: { dark: colorDark, light: colorLight },
			});
			if (cancelled) return;
			// qrcode's canvas renderer sets canvas.style.width/height to match the (now DPR-scaled)
			// backing store — pin them back to the intended CSS box, else the code renders oversized.
			canvas.style.width = `${displaySize}px`;
			canvas.style.height = `${displaySize}px`;
			setRendered(true);
		})().catch((e: any) => console.error("[AppsFlyer] QR canvas render failed:", { url, error: e?.message || e }));

		return () => {
			cancelled = true;
		};
	}, [active, url, size, colorDark, colorLight, canvasRef]);

	return rendered;
}
